/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-useless-catch */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2024, Ouster, Inc.
 * All rights reserved.
 */
import * as Sentry from '@sentry/react';
import {
  action,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  thunk,
} from 'easy-peasy';

import {
  UserWithRole,
  UserRole,
  OrgSpaceUsage,
  OrganizationInfo,
} from '../types';
import { BASE_ORG_API } from '../utils/constants';
import { fetchWithAuthV2 } from '../utils/requests';

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

export type State = {
  orgUserList: UserWithRole[];
  orgInviteeList: UserWithRole[];
  orgSpaceUsage: OrgSpaceUsage;
  fullOrgList: OrganizationInfo[];
};

export const defaultState: State = {
  orgUserList: [],
  orgInviteeList: [],
  orgSpaceUsage: { allowed_space: 0, space_used: 0 },
  fullOrgList: [],
};

export interface OrganizationStateModel extends State {
  setOrgUserList: EzPzAction<OrganizationStateModel, UserWithRole[]>;
  setOrgInviteeList: EzPzAction<OrganizationStateModel, UserWithRole[]>;
  setFullOrgList: EzPzAction<OrganizationStateModel, OrganizationInfo[]>;
  setOrgSpaceUsage: EzPzAction<OrganizationStateModel, OrgSpaceUsage>;
  fetchOrgUserList: EzPzThunk<OrganizationStateModel, number>;
  fetchOrgInvitationList: EzPzThunk<OrganizationStateModel, string>;
  fetchOrgSpaceUsage: EzPzThunk<OrganizationStateModel, number>;
  fetchFullOrgList: EzPzThunk<OrganizationStateModel, void>;
  createOrg: EzPzThunk<OrganizationStateModel, string>;
  ousterUserCreateOrg: EzPzThunk<
    OrganizationStateModel,
    { orgName: string; is_provisioned?: boolean }
  >;
  inviteUserToOrg: EzPzThunk<
    OrganizationStateModel,
    { email: string; role: UserRole; organization_id: number }
  >;
  reinviteUserToOrg: EzPzThunk<OrganizationStateModel, string>;
  updateUserRole: EzPzThunk<OrganizationStateModel, UserWithRole>;
  updateOrgName: EzPzThunk<
    OrganizationStateModel,
    { name: string; organization_id: number }
  >;
  addInvitedUser: EzPzThunk<
    OrganizationStateModel,
    { organization_uuid: string; invitation_id: string }
  >;
  deleteUserFromOrg: EzPzThunk<OrganizationStateModel, UserWithRole>;
  deleteInvitedUserFromOrg: EzPzThunk<
    OrganizationStateModel,
    { organization_uuid: string; invitation_uuid: string }
  >;
  completeOrgHandover: EzPzThunk<OrganizationStateModel, number>;
}

export const model: OrganizationStateModel = {
  ...defaultState,

  setOrgUserList: action((state, orgUserList) => {
    orgUserList.sort((a, b) => a.role.localeCompare(b.role));
    state.orgUserList = orgUserList;
  }),
  setOrgInviteeList: action((state, orgInviteeList) => {
    state.orgInviteeList = orgInviteeList;
  }),
  setFullOrgList: action((state, fullOrgList) => {
    state.fullOrgList = fullOrgList;
  }),
  setOrgSpaceUsage: action((state, orgUsage) => {
    state.orgSpaceUsage = orgUsage;
  }),
  fetchFullOrgList: thunk(async (actions) => {
    // only Ouster admin can fetch all organizations
    const path = `${BASE_ORG_API}/search`;
    try {
      const orgs = (await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'failed to fetch Full org List',
      })) as {
        results: OrganizationInfo[];
      };
      actions.setFullOrgList(orgs.results);
    } catch (err) {
      actions.setFullOrgList([]);
      console.error('Failed to load user in organizations: ', err);
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'fetchFullOrgList' },
      });
    }
  }),
  fetchOrgUserList: thunk(async (actions, org_id) => {
    // get all user under a specific organization
    const path = `${BASE_ORG_API}/${org_id}/users/`;

    try {
      const users = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'failed to fetch Org User List',
      });
      actions.setOrgUserList(users);
    } catch (err) {
      actions.setOrgUserList([]);
      console.error('Failed to load user in organizations: ', err);
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'fetchOrgUserList' },
        extra: { org_id },
      });
    }
  }),
  fetchOrgInvitationList: thunk(async (actions, org_uuid) => {
    // get invited but not accepted users under a specific organization
    const path = `${BASE_ORG_API}/${org_uuid}/invitations/`;
    try {
      const invitedUsers = (await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'failed to get organization invitation list',
      })) as UserWithRole[];
      const users: UserWithRole[] = invitedUsers
        .filter((user) => user.invitation_status === 'CREATED')
        .map((user) => ({
          ...user,
          invitation_link: `${BASE_ORG_API}/${org_uuid}/invitations/reinvite/${user.uuid}`,
        }));
      actions.setOrgInviteeList(users);
    } catch (err) {
      actions.setOrgInviteeList([]);
      console.error('Failed to load invited user in organizations: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'OrganizationStateModel',
          thunk: 'fetchOrgInvitationList',
        },
        extra: { org_uuid },
      });
    }
  }),
  fetchOrgSpaceUsage: thunk(async (actions, org_id) => {
    // get invited but not accepted users under a specific organization
    const path = `/api/v1/space_util/organizations/${org_id}`;
    try {
      const usage = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'failed to get organization space utilization',
      });

      actions.setOrgSpaceUsage(usage);
    } catch (err) {
      console.error('Failed to load organization space utilization: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'OrganizationStateModel',
          thunk: 'fetchOrgSpaceUsage',
        },
        extra: { org_id },
      });
      /* 
        When we fail to get space info, set the allowed_space to -999, so the FE will show error message to inform user
      */
      actions.setOrgSpaceUsage({ allowed_space: -999, space_used: 0 });
    }
  }),
  createOrg: thunk(async (_, name) => {
    const path = `${BASE_ORG_API}/`;
    try {
      const organizationData = await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to create organization',
        body: JSON.stringify({ name }),
      });
      return organizationData;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'createOrg' },
        extra: { name },
      });
      throw err;
    }
  }),
  ousterUserCreateOrg: thunk(
    async (
      _,
      { orgName, is_provisioned }: { orgName: string; is_provisioned?: boolean }
    ) => {
      const path = `${BASE_ORG_API}/ouster-create`;
      try {
        const organizationData = await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage:
            'Failed to allow Ouster user to create organization',
          body: JSON.stringify({ name: orgName, is_provisioned }),
        });
        return organizationData;
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            section: 'OrganizationStateModel',
            thunk: 'ousterUserCreateOrg',
          },
          extra: { name: orgName, is_provisioned },
        });
        throw err;
      }
    }
  ),
  inviteUserToOrg: thunk(async (_, { email, role, organization_id }) => {
    const path = `${BASE_ORG_API}/${organization_id}/users/`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to invite user',
        body: JSON.stringify({ email, role }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'inviteUserToOrg' },
        extra: { email, role, organization_id },
      });
      throw err;
    }
  }),
  reinviteUserToOrg: thunk(async (_, invitation_link) => {
    try {
      await fetchWithAuthV2(invitation_link, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to reinvite user',
        body: JSON.stringify({ status: 'RESENT' }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'reinviteUserToOrg' },
        extra: { invitation_link },
      });
      throw err;
    }
  }),
  updateUserRole: thunk(async (actions, { id, organization_id, role }) => {
    const path = `${BASE_ORG_API}/${organization_id}/users/${id}`;
    try {
      await fetchWithAuthV2(path, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to update user role',
        body: JSON.stringify({ role }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'updateUserRole' },
        extra: { id, organization_id, role },
      });
      throw err;
    }
  }),
  updateOrgName: thunk(async (_, { name, organization_id }) => {
    const path = `${BASE_ORG_API}/${organization_id}`;
    try {
      const org = await fetchWithAuthV2(path, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to update organization name',
        body: JSON.stringify({ name }),
      });

      return org;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'updateOrgName' },
        extra: { name, organization_id },
      });
      throw err;
    }
  }),
  deleteUserFromOrg: thunk(async (actions, { id, organization_id }) => {
    const path = `${BASE_ORG_API}/${organization_id}/users/${id}`;
    try {
      await fetchWithAuthV2(path, {
        method: 'DELETE',
        fallbackErrorMessage: 'Failed to delete organization user',
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'deleteUserFromOrg' },
        extra: { id, organization_id },
      });
      throw err;
    }
  }),
  deleteInvitedUserFromOrg: thunk(
    async (actions, { organization_uuid, invitation_uuid }) => {
      const path = `${BASE_ORG_API}/${organization_uuid}/invitations/${invitation_uuid}`;
      try {
        await fetchWithAuthV2(path, {
          method: 'DELETE',
          fallbackErrorMessage: 'failed to delete invited user',
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            section: 'OrganizationStateModel',
            thunk: 'deleteInvitedUserFromOrg',
          },
          extra: { organization_uuid, invitation_uuid },
        });
        throw err;
      }
    }
  ),
  addInvitedUser: thunk(async (_, { invitation_id, organization_uuid }) => {
    const path = `${BASE_ORG_API}/${organization_uuid}/invitations/${invitation_id}`;
    try {
      await fetchWithAuthV2(path, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to add invited user',
        body: JSON.stringify({ status: 'ACCEPTED' }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'OrganizationStateModel', thunk: 'addInvitedUser' },
        extra: { invitation_id, organization_uuid },
      });
      throw err;
    }
  }),
  completeOrgHandover: thunk(async (_, organization_id) => {
    const path = `${BASE_ORG_API}/${organization_id}/handover`;
    try {
      const result = await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to complete organization handover',
      });
      return result;
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          section: 'OrganizationStateModel',
          thunk: 'completeOrgHandover',
        },
        extra: { organization_id },
      });
      throw err;
    }
  }),
};
